<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type WatchStopHandle,
} from 'vue'
import NavHeader from '@/components/NavHeader.vue'
import AppInnerAlert from '@/components/AppInnerAlert.vue'
import { keepAliveIncludes, newHeaderEnabled } from '@/utils/keep-alive-config'
import type { MessageType } from '@/types/message'
import { getSearchParam } from '@/utils/urlQuery'
import { useTabRedDotStore } from '@/stores/tabRedDot'
import { clearAiUnreadTarget, getTabTargetQuery, mergeAiUnreadTarget, shouldConsumeAiTarget, TAB_CODE_BY_PATH } from '@/stores/tabRedDotPolicy'
import { AI_RED_DOT_DETECTED_EVENT } from '@/views/Chat/chatSyncPolicy'
import { canShowAiInnerAlertOnPath } from '@/stores/aiInnerAlertPolicy'


// 注意：本文件不在顶层 import @/utils/bridge 或 @/utils/navigation，
// 这两个模块都会触发一条 bridge → router → user → chat → bridge 的循环依赖，
// 导致 chat.ts 模块顶层调用 isApp() 时遇到 TDZ。bridge 必须由 main.ts 顶层
// 第一时间加载完成。本文件需要 bridge 能力时，统一用动态 import 推迟到
// 事件回调阶段。

const route = useRoute()
const router = useRouter()
const tabRedDotStore = useTabRedDotStore()
const WeComQrDialog = defineAsyncComponent(() => import('@/components/WeComQrDialog.vue'))
// 评审更新（2026-07-14）：本期不触发弹窗，保留代码
// const agreementDialogRef = ref<InstanceType<typeof AgreementUpdateDialog> | null>(null)

/** 新结构 3 Tab 配置（icon 走 iconfont 类名，由 NavHeader 内部渲染并染色） */
const NAV_TABS = [
  { title: '账单', path: '/bill', icon: 'qx-QX-zhangdan' },
  { title: '明细', path: '/details', icon: 'qx-QX-mingxi' },
  { title: '智能助手', path: '/chat', icon: 'qx-QX-zhinengzhushou' },
]

const isTabRoute = computed(() =>
  NAV_TABS.some((t) => t.path === route.path),
)

const navActive = computed(() => {
  const idx = NAV_TABS.findIndex((t) => t.path === route.path)
  return idx >= 0 ? idx : 0
})

const navRedDots = computed(() => NAV_TABS.map((tab) => {
  const code = TAB_CODE_BY_PATH[tab.path]
  return code ? tabRedDotStore.redDots[code] : false
}))

const getRouteSource = () => {
  const source = route.query.source
  if (Array.isArray(source)) {
    return source.find((item): item is string => typeof item === 'string' && item.length > 0) || ''
  }
  return typeof source === 'string' ? source : ''
}

const getCurrentSource = () => getRouteSource() || getSearchParam('source')
const isQxAppWebView = () => /hncyQxApp/i.test(navigator.userAgent)
const isCurrentDongchaSource = () => getCurrentSource().toLowerCase() === 'dongcha'
const isDongchaH5 = ref(!isQxAppWebView() && isCurrentDongchaSource())

/**
 * 根容器 class —— 新结构下由 .app-root 统一渲染背景图，
 * 让 NavHeader 和下方内容共享同一张连续背景；旧结构 / 非 Tab 路由
 * 不画背景（沿用各自页面原有背景）。
 * 默认与首页一致 (bg-default)，仅 chat 走自己的渐变背景。
 */
const rootClass = computed(() => {
  const classes = ['app-root']
  if (!newHeaderEnabled.value || !isTabRoute.value || isDongchaH5.value) return classes
  classes.push('with-bg')
  if (route.path === '/chat') classes.push('bg-chat')
  else classes.push('bg-default')
  return classes
})

/** 菜单按钮显示条件：智能助手激活（H5 也展示动画，点击在 H5 下跳历史记录） */
const showMenu = computed(() => route.path === '/chat')
const showWeComExitDialog = ref(false)
const weComExitQrCodeUrl = ref('')
const showWeComAfterAddDialog = ref(false)
const weComAfterAddQrCodeUrl = ref('')
let handleWeComExitDialogClose = () => {
  showWeComExitDialog.value = false
}
let handleWeComExitQrLoad = () => {}
let handleWeComExitQrError = () => {}
let handleWeComAfterAddDialogClose = () => {
  showWeComAfterAddDialog.value = false
}
let handleWeComAfterAddQrLoad = () => {}
let handleWeComAfterAddQrError = () => {}
const hasUnreadMessage = ref(false)
const showMessageBubble = ref(false)
const latestUnreadMessageId = ref('')
const latestUnreadMessageType = ref<MessageType | ''>('')
const lastBubbleExposureMessageId = ref('')
const showAiInnerAlert = ref(false)
let aiUnreadAlertLookupPending = false
let pendingAiAlertAckContentId = ''

let messageStoreRef: null | {
  hasUnread: boolean
  latestUnreadMessageId: string
  latestUnreadMessageType: MessageType | ''
  shouldShowBubble: boolean
  fetchUnreadStatus: () => Promise<unknown>
  loadBubbleLastShownId: () => Promise<void>
  markBubbleShown: (messageId?: string) => void
} = null

const syncMessageHeaderState = () => {
  if (isDongchaH5.value) {
    hasUnreadMessage.value = false
    latestUnreadMessageId.value = ''
    latestUnreadMessageType.value = ''
    showMessageBubble.value = false
    return
  }
  if (!messageStoreRef) return
  hasUnreadMessage.value = messageStoreRef.hasUnread
  latestUnreadMessageId.value = messageStoreRef.latestUnreadMessageId
  latestUnreadMessageType.value = messageStoreRef.latestUnreadMessageType
  showMessageBubble.value = messageStoreRef.shouldShowBubble
}

const updateDongchaH5State = async () => {
  const { isApp } = await import('@/utils/bridge')
  if (isApp()) {
    isDongchaH5.value = false
    return
  }
  isDongchaH5.value = isCurrentDongchaSource()
}

const shouldFetchMessageStatus = async () => {
  if (isDongchaH5.value) return false
  if (!MAIN_TAB_PATHS.has(route.path)) return false
  const { isLogin } = await import('@/utils/auth')
  return isLogin()
}

const trackMessageBubbleExposureIfNeeded = async () => {
  if (isDongchaH5.value) return
  if (!showMessageBubble.value || !latestUnreadMessageId.value) return
  if (lastBubbleExposureMessageId.value === latestUnreadMessageId.value) return
  lastBubbleExposureMessageId.value = latestUnreadMessageId.value
  const { track } = await import('@/plugins/event-tracker')
  track('home_new_message_bubble_exposure', {
    bubble_type: getMessageTypeText(latestUnreadMessageType.value),
  })
}

const handleTabChange = async (idx: number) => {
  const target = NAV_TABS[idx]
  if (!target) return
  const tabCode = TAB_CODE_BY_PATH[target.path]
  if (!tabCode) return
  if (route.path !== target.path) {
    const { isApp, tcVibrate } = await import('@/utils/bridge')
    if (isApp()) void tcVibrate()
  }
  const { isLogin } = await import('@/utils/auth')
  const loggedIn = await isLogin()
  if (!loggedIn) tabRedDotStore.reset()
  const hadRedDot = loggedIn && tabRedDotStore.redDots[tabCode]
  const { track } = await import('@/plugins/event-tracker')
  track('home_tab_red_dot_click', { tab_code: tabCode, has_red_dot: hadRedDot })
  let targetQuery = loggedIn
    ? getTabTargetQuery(tabCode, route.query)
    : clearAiUnreadTarget(route.query)
  if (shouldConsumeAiTarget(tabCode, hadRedDot, targetQuery)) {
    const unreadTarget = await tabRedDotStore.consumeAiTarget().catch(() => null)
    if (unreadTarget?.sessionId && unreadTarget.contentId) {
      const { useChatStore } = await import('@/stores/chat')
      const chatStore = useChatStore()
      await chatStore.prefetchTargetSession(
        String(unreadTarget.sessionId),
        String(unreadTarget.contentId),
      ).catch(() => false)
    }
    targetQuery = mergeAiUnreadTarget(targetQuery, unreadTarget)
  }
  if (route.path !== target.path) {
    const targetRoute = {
      path: target.path,
      query: targetQuery,
    }
    if (isDongchaH5.value) {
      router.replace(targetRoute)
    } else {
      router.push(targetRoute)
    }
  }
  if (hadRedDot) {
    void tabRedDotStore.clear(tabCode)
      .catch(() => false)
      .then((cleared) => {
        track('home_tab_red_dot_clear_result', { tab_code: tabCode, success: cleared })
      })
  }
  tabRedDotStore.fetchRedDots().catch(() => undefined)
}

const canShowAiInnerAlert = () => canShowAiInnerAlertOnPath(route.path)

const hideAiInnerAlert = () => {
  showAiInnerAlert.value = false
}

const acknowledgePendingAiAlert = async () => {
  if (!pendingAiAlertAckContentId) return
  const contentId = pendingAiAlertAckContentId
  const acknowledged = await tabRedDotStore.acknowledgeAiTargetShown(contentId).catch(() => false)
  if (acknowledged && pendingAiAlertAckContentId === contentId) pendingAiAlertAckContentId = ''
}

const tryShowAiInnerAlert = async () => {
  if (!tabRedDotStore.redDots.AI || !canShowAiInnerAlert()) return
  if (pendingAiAlertAckContentId) {
    await acknowledgePendingAiAlert()
    return
  }
  if (aiUnreadAlertLookupPending) return
  aiUnreadAlertLookupPending = true
  try {
    const target = await tabRedDotStore.getUnshownAiTarget()
    if (!target?.contentId || !tabRedDotStore.redDots.AI || !canShowAiInnerAlert()) return
    showAiInnerAlert.value = true
    pendingAiAlertAckContentId = String(target.contentId)
    await acknowledgePendingAiAlert()
  } finally {
    aiUnreadAlertLookupPending = false
  }
}

const handleAiInnerAlertClick = async () => {
  hideAiInnerAlert()
  await handleTabChange(2)
}

const handleMineClick = async () => {
  // 动态 import 避免顶层触发 bridge/router 循环依赖
  const [{ navigateTo }, { isApp, tcVibrate }] = await Promise.all([
    import('@/utils/navigation'),
    import('@/utils/bridge'),
  ])
  if (isApp()) void tcVibrate()
  navigateTo('/mine', { replaceInH5: false })
}

const getMessageTypeText = (messageType: MessageType | '') => {
  if (messageType === 'MONTHLY_REPORT') return '月报'
  return '周报'
}

const messageBubbleTypeText = computed(() => getMessageTypeText(latestUnreadMessageType.value))

const openReportByMessage = async (messageType: MessageType | '', messageId: string) => {
  const { navigateTo } = await import('@/utils/navigation')
  if (messageType === 'MONTHLY_REPORT') {
    navigateTo({ path: '/monthlyReport', query: { messageId, isUnread: '1', from: 'homeBubble' } }, { replaceInH5: false })
    return
  }
  navigateTo({ path: '/weeklyReport', query: { messageId, isUnread: '1', from: 'homeBubble' } }, { replaceInH5: false })
}

const handleMessageClick = async () => {
  if (isDongchaH5.value) return
  const { isApp, tcVibrate } = await import('@/utils/bridge')
  if (isApp()) void tcVibrate()
  const { track } = await import('@/plugins/event-tracker')
  track('home_message_icon_click', {
    has_unread_message: hasUnreadMessage.value,
  })
  const { navigateTo } = await import('@/utils/navigation')
  navigateTo('/messageCenter', { replaceInH5: false })
}

const handleMessageBubbleClose = async () => {
  if (messageStoreRef && latestUnreadMessageId.value) {
    messageStoreRef.markBubbleShown(latestUnreadMessageId.value)
  }
  showMessageBubble.value = false
  const { track } = await import('@/plugins/event-tracker')
  track('home_new_message_bubble_close', {})
}

const handleMessageBubbleClick = async () => {
  if (isDongchaH5.value) return
  if (!latestUnreadMessageId.value) return
  const { isApp, tcVibrate } = await import('@/utils/bridge')
  if (isApp()) void tcVibrate()
  const targetType = messageBubbleTypeText.value
  if (messageStoreRef) {
    messageStoreRef.markBubbleShown(latestUnreadMessageId.value)
  }
  showMessageBubble.value = false
  const { track } = await import('@/plugins/event-tracker')
  track('home_new_message_bubble_click', {
    target_type: targetType,
  })
  openReportByMessage(latestUnreadMessageType.value, latestUnreadMessageId.value)
}

const handleMenuClick = async () => {
  // 动态 import 避免顶层触发 bridge 循环依赖
  const { isApp, tcOpenTheMenu, tcVibrate } = await import('@/utils/bridge')
  if (isApp()) {
    void tcVibrate()
    tcOpenTheMenu()
  } else {
    // 纯 H5 环境：跳转到历史记录页
    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement) {
      activeElement.blur()
    }
    const { navigateTo } = await import('@/utils/navigation')
    navigateTo('/history-chat', { replaceInH5: false })
  }
}

/**
 * 1.2.0+ App 单 WebView 架构下，二级页（如 /addBill）通过 localStorage 留下
 * `qx-pending-home-tab` flag 告知主 Tab「回前台后切到此 Tab」。本函数幂等消费一次。
 * 浏览器 / 非主 Tab 路由也安全：消费后立即 removeItem，不会跨页误触发。
 */
const PENDING_HOME_TAB_KEY = 'qx-pending-home-tab'
const consumePendingHomeTab = () => {
  let pending: string | null = null
  try {
    pending = localStorage.getItem(PENDING_HOME_TAB_KEY)
  } catch {
    return
  }
  if (!pending) return
  try {
    localStorage.removeItem(PENDING_HOME_TAB_KEY)
  } catch {
    // ignore
  }
  if (NAV_TABS.some((t) => t.path === pending) && route.path !== pending) {
    router.replace(pending)
  }
}

const MAIN_TAB_PATHS = new Set(NAV_TABS.map((tab) => tab.path))
const TAB_RED_DOT_POLL_INTERVAL_MS = 3000
let tabRedDotPollTimer: ReturnType<typeof setInterval> | null = null

const notifyAiPageIfUnread = (redDots: Record<string, boolean>) => {
  if (route.path === '/chat' && redDots.AI) {
    window.dispatchEvent(new CustomEvent(AI_RED_DOT_DETECTED_EVENT))
  }
}

const fetchTabRedDotsAndNotify = () => tabRedDotStore.fetchRedDots()
  .then((redDots) => {
    notifyAiPageIfUnread(redDots)
    if (redDots.AI && canShowAiInnerAlert()) void tryShowAiInnerAlert()
    return redDots
  })

const refreshTabRedDotsIfEligible = () => {
  if (document.hidden || !MAIN_TAB_PATHS.has(route.path)) return
  fetchTabRedDotsAndNotify().catch(() => undefined)
}

const stopTabRedDotPolling = () => {
  if (tabRedDotPollTimer === null) return
  clearInterval(tabRedDotPollTimer)
  tabRedDotPollTimer = null
}

const startTabRedDotPolling = () => {
  stopTabRedDotPolling()
  if (document.hidden) return
  tabRedDotPollTimer = setInterval(refreshTabRedDotsIfEligible, TAB_RED_DOT_POLL_INTERVAL_MS)
}

const handleTabRedDotVisibilityChange = () => {
  if (document.hidden) {
    stopTabRedDotPolling()
    return
  }
  refreshTabRedDotsIfEligible()
  startTabRedDotPolling()
}

const switchMainTabFromNative = (tabName: string) => {
  if (!MAIN_TAB_PATHS.has(tabName) || route.path === tabName) return
  router.replace(tabName)
}

// 评审更新（2026-07-14）：本期不触发弹窗，保留代码
// const checkAgreementUpdate = async () => {
//   if (!isLogin() || !route.meta.requiresAuth) return
//   const { isApp } = await import('@/utils/bridge')
//   if (isApp()) return
//   await nextTick()
//   await agreementDialogRef.value?.checkAndShow()
// }

watch(
  () => route.path,
  (newPath, oldPath) => {
    if (!canShowAiInnerAlert()) hideAiInnerAlert()
    else void tryShowAiInnerAlert()
    if (newPath !== oldPath && MAIN_TAB_PATHS.has(newPath)) {
      fetchTabRedDotsAndNotify().catch(() => undefined)
    }
  },
)

watch(
  () => tabRedDotStore.redDots.AI,
  (hasUnread) => {
    if (!hasUnread) {
      hideAiInnerAlert()
      return
    }
    void tryShowAiInnerAlert()
  },
)

/** tcAppOnAppear 监听器的取消函数（页面卸载时清理） */
let unsubAppOnAppear: (() => void) | null = null
let unregisterWeComAfterAddPopup: (() => void) | null = null
const stopWeComExitWatches: WatchStopHandle[] = []
const stopWeComAfterAddWatches: WatchStopHandle[] = []

onMounted(async () => {
  document.addEventListener('visibilitychange', handleTabRedDotVisibilityChange)
  startTabRedDotPolling()
  fetchTabRedDotsAndNotify()
    .then(async (redDots) => {
      const { track } = await import('@/plugins/event-tracker')
      Object.entries(redDots).forEach(([tabCode, hasRedDot]) => {
        if (hasRedDot) track('home_tab_red_dot_exposure', { tab_code: tabCode })
      })
    })
    .catch(() => undefined)
  await updateDongchaH5State()
  consumePendingHomeTab()
  const { isApp, tcAppOnAppear, tcTabChange } = await import('@/utils/bridge')
  if (isApp()) {
    tcTabChange((tabName) => {
      switchMainTabFromNative(tabName)
      consumePendingHomeTab()
    })
    unsubAppOnAppear = tcAppOnAppear(() => {
      consumePendingHomeTab()
      updateDongchaH5State()
        .then(() => shouldFetchMessageStatus())
        .then((isLoggedIn) => {
          if (isLoggedIn) {
            messageStoreRef?.fetchUnreadStatus().catch(() => undefined)
          }
        })
        .catch(() => undefined)
    })
  } else {
    const { useWeComExitIntercept } = await import('@/composables/useWeComExitIntercept')
    const weComExitIntercept = useWeComExitIntercept()
    const { useWeComAfterAddPopup } = await import('@/composables/useWeComAfterAddPopup')
    const weComAfterAddPopup = useWeComAfterAddPopup()
    stopWeComExitWatches.push(
      watch(weComExitIntercept.showWeComQrDialog, (value) => {
        showWeComExitDialog.value = value
      }, { immediate: true }),
      watch(weComExitIntercept.weComQrCodeUrl, (value) => {
        weComExitQrCodeUrl.value = value
      }, { immediate: true }),
    )
    handleWeComExitDialogClose = weComExitIntercept.handleExitDialogClose
    handleWeComExitQrLoad = weComExitIntercept.trackWeComQrLoad
    handleWeComExitQrError = weComExitIntercept.trackWeComQrError
    stopWeComAfterAddWatches.push(
      watch(weComAfterAddPopup.showWeComQrDialog, (value) => {
        showWeComAfterAddDialog.value = value
      }, { immediate: true }),
      watch(weComAfterAddPopup.weComQrCodeUrl, (value) => {
        weComAfterAddQrCodeUrl.value = value
      }, { immediate: true }),
    )
    handleWeComAfterAddDialogClose = weComAfterAddPopup.handleAfterAddDialogClose
    handleWeComAfterAddQrLoad = weComAfterAddPopup.trackWeComQrLoad
    handleWeComAfterAddQrError = weComAfterAddPopup.trackWeComQrError
    unregisterWeComAfterAddPopup = weComAfterAddPopup.registerAfterAddPopup(router)
  }
  try {
    const { useMessageStore } = await import('@/stores/message')
    const messageStore = useMessageStore()
    messageStoreRef = messageStore
    await messageStore.loadBubbleLastShownId()
    if (await shouldFetchMessageStatus()) {
      await messageStore.fetchUnreadStatus().catch(() => undefined)
    }
    syncMessageHeaderState()
    watch(
      () => [
        isDongchaH5.value,
        messageStore.hasUnread,
        messageStore.latestUnreadMessageId,
        messageStore.latestUnreadMessageType,
        messageStore.shouldShowBubble,
      ],
      () => {
        syncMessageHeaderState()
        trackMessageBubbleExposureIfNeeded()
      },
    )

    watch(
      () => route.fullPath,
      () => {
        updateDongchaH5State()
          .then(syncMessageHeaderState)
          .catch(() => undefined)
      },
    )

    watch(
      () => route.path,
      async (newPath, oldPath) => {
        await updateDongchaH5State().catch(() => undefined)
        if (!MAIN_TAB_PATHS.has(newPath)) return
        // 仅从二级页返回主 Tab 时触发，Tab 间切换不触发（对齐 App tcAppOnAppear）
        if (oldPath && MAIN_TAB_PATHS.has(oldPath)) return
        if (!(await shouldFetchMessageStatus())) return
        messageStore.fetchUnreadStatus().catch(() => undefined)
      },
    )

    // H5 场景：对齐 App tcAppOnAppear —— 从后台回前台时刷新未读状态
    const { isApp: isAppEnv } = await import('@/utils/bridge')
    if (!isAppEnv()) {
      const onH5Visible = async () => {
        if (document.visibilityState !== 'visible') return
        if (!MAIN_TAB_PATHS.has(route.path)) return
        if (!(await shouldFetchMessageStatus())) return
        messageStore.fetchUnreadStatus().catch(() => undefined)
      }
      document.addEventListener('visibilitychange', onH5Visible)
      onBeforeUnmount(() => {
        document.removeEventListener('visibilitychange', onH5Visible)
      })
    }

    await nextTick()
    trackMessageBubbleExposureIfNeeded()
  } catch (error) {
    console.error('初始化消息入口失败:', error)
  }
  // 评审更新（2026-07-14）：本期不触发弹窗
  // await checkAgreementUpdate()
})

// 评审更新（2026-07-14）：本期不触发弹窗，保留代码
// watch(
//   () => route.fullPath,
//   () => {
//     checkAgreementUpdate()
//   },
// )

onBeforeUnmount(() => {
  hideAiInnerAlert()
  stopTabRedDotPolling()
  document.removeEventListener('visibilitychange', handleTabRedDotVisibilityChange)
  if (unsubAppOnAppear) {
    unsubAppOnAppear()
    unsubAppOnAppear = null
  }
  if (unregisterWeComAfterAddPopup) {
    unregisterWeComAfterAddPopup()
    unregisterWeComAfterAddPopup = null
  }
  stopWeComExitWatches.splice(0).forEach((stop) => stop())
  stopWeComAfterAddWatches.splice(0).forEach((stop) => stop())
})
</script>

<template>
  <div :class="rootClass">
    <AppInnerAlert
      v-model="showAiInnerAlert"
      title="智能助手"
      content="已为账单生成了智能分析，点击查看"
      @click="handleAiInnerAlertClick"
    />
    <NavHeader
      v-if="newHeaderEnabled && isTabRoute && !isDongchaH5"
      :model-value="navActive"
      :tabs="NAV_TABS"
      :red-dots="navRedDots"
      :show-menu="showMenu"
      :show-mine="true"
      :show-message-entry="!isDongchaH5"
      :has-unread-message="hasUnreadMessage"
      :message-bubble-visible="showMessageBubble"
      :message-bubble-type="messageBubbleTypeText"
      @change="handleTabChange"
      @menu-click="handleMenuClick"
      @mine-click="handleMineClick"
      @message-click="handleMessageClick"
      @message-bubble-close="handleMessageBubbleClose"
      @message-bubble-click="handleMessageBubbleClick"
    />
    <RouterView v-slot="{ Component }">
      <KeepAlive :include="keepAliveIncludes">
        <component :is="Component" />
      </KeepAlive>
    </RouterView>
    <WeComQrDialog
      v-if="showWeComExitDialog"
      v-model:show="showWeComExitDialog"
      scene="exit"
      :qr-code-url="weComExitQrCodeUrl"
      @close="handleWeComExitDialogClose"
      @qr-load="handleWeComExitQrLoad"
      @qr-error="handleWeComExitQrError"
    />
    <WeComQrDialog
      v-if="showWeComAfterAddDialog"
      v-model:show="showWeComAfterAddDialog"
      scene="after_add"
      :qr-code-url="weComAfterAddQrCodeUrl"
      @close="handleWeComAfterAddDialogClose"
      @qr-load="handleWeComAfterAddQrLoad"
      @qr-error="handleWeComAfterAddQrError"
    />
    <!-- <AgreementUpdateDialog ref="agreementDialogRef" /> -->  </div>
</template>

<style lang="scss" scoped>
/* App 根容器：flex column，让 NavHeader 占据自然高度，RouterView 内容占剩余空间 */
.app-root {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 新结构 Tab 路由下，由 .app-root 统一渲染背景，确保 NavHeader 与下方内容连成一张背景 */
.app-root.with-bg {
  background-size: 750px 600px;
  background-position: top center;
  background-repeat: no-repeat;

  &.bg-default {
    background-image: url('@/assets/images/basic/page-bg.png');
  }
  &.bg-chat {
    background-image: url('@/assets/images/chatView/chat-bg.png');
    background-size: 750px 100vh;
  }
}
</style>
